import { Vector2, type Vector3Like } from 'three';
import ParticleEmitter from './ParticleEmitter';
import { type ParticleEmitterID } from './ParticleEmitterConstants';
import {
  type ParticleEmitterCoreOptions,
  type ParticleEmitterEmissionOverrides,
} from './ParticleEmitterCore';
import ParticleStats from './ParticleStats';
import { RendererEventType, type RendererEventPayload } from '../core/Renderer';
import EventRouter from '../events/EventRouter';
import Game from '../Game';
import { type DeserializedParticleEmitter } from '../network/Deserializer';
import { NetworkManagerEventType, type NetworkManagerEventPayload } from '../network/NetworkManager';
import { toVector3 } from '../three/utils';

// Working variables
const fromVec2 = new Vector2();
const toVec2 = new Vector2();

export default class ParticleEmitterManager {
  private _game: Game;
  private _particleEmitters: Map<ParticleEmitterID, ParticleEmitter> = new Map();
  private _pendingEmitterUpdates: Map<ParticleEmitterID, DeserializedParticleEmitter[]> = new Map();

  constructor(game: Game) {
    this._game = game;
    this._setupEventListeners();
  }

  private _setupEventListeners(): void {
    EventRouter.instance.on(
      RendererEventType.Animate,
      this._onAnimate,
    );

    EventRouter.instance.on(
      NetworkManagerEventType.ParticleEmittersPacket,
      this._onParticleEmittersPacket,
    );
  }

  private _onAnimate = (payload: RendererEventPayload.IAnimate): void => {
    // Update stats
    ParticleStats.emitterCount = this._particleEmitters.size;
    ParticleStats.drawCalls = 0;

    for (const particleEmitter of this._particleEmitters.values()) {
      particleEmitter.update(payload.frameDeltaS);
      if (particleEmitter.mesh.visible) {
        ParticleStats.drawCalls++;
      }
    }

    if (!this._game.settingsManager.qualityPerfTradeoff.viewDistance.enabled) {
      return;
    }

    const viewDistance = this._game.renderer.viewDistance;
    const cameraPos = this._game.camera.activeCamera.position;
    fromVec2.set(cameraPos.x, cameraPos.z);

    // TODO: Since emitted particles do not follow the ParticleEmitter's position,
    // this ViewDistance logic may need to be reconsidered. For example, if a
    // ParticleEmitter that was nearby moves far away, its already-emitted particles
    // may still be near and should remain visible, but they will be made invisible
    // due to the emitter's new position being outside the view distance.
    this._particleEmitters.forEach((particleEmitter) => {
      if (particleEmitter.hasDynamicPlaySources) {
        particleEmitter.setVisible(true);
        return;
      }

      const pos = particleEmitter.mesh.position;
      particleEmitter.setVisible(fromVec2.distanceTo(toVec2.set(pos.x, pos.z)) <= viewDistance);
    });
  }

  private _onParticleEmittersPacket = (payload: NetworkManagerEventPayload.IParticleEmittersPacket): void => {
    for (const deserializedParticleEmitter of payload.deserializedParticleEmitters) {
      this._updateParticleEmitter(deserializedParticleEmitter);
    }
  }

  private _toCoreOptions(source: DeserializedParticleEmitter): ParticleEmitterCoreOptions {
    const options: ParticleEmitterCoreOptions = {};

    if (source.alphaTest !== undefined) options.alphaTest = source.alphaTest;
    if (source.colorEnd !== undefined) options.colorEnd = source.colorEnd;
    if (source.colorEndVariance !== undefined) options.colorEndVariance = source.colorEndVariance;
    if (source.colorIntensityEnd !== undefined) options.colorIntensityEnd = source.colorIntensityEnd;
    if (source.colorIntensityEndVariance !== undefined) options.colorIntensityEndVariance = source.colorIntensityEndVariance;
    if (source.colorIntensityStart !== undefined) options.colorIntensityStart = source.colorIntensityStart;
    if (source.colorIntensityStartVariance !== undefined) options.colorIntensityStartVariance = source.colorIntensityStartVariance;
    if (source.colorStart !== undefined) options.colorStart = source.colorStart;
    if (source.colorStartVariance !== undefined) options.colorStartVariance = source.colorStartVariance;
    if (source.gravity !== undefined) options.gravity = toVector3(source.gravity);
    if (source.lifetime !== undefined) options.lifetime = source.lifetime;
    if (source.lifetimeVariance !== undefined) options.lifetimeVariance = source.lifetimeVariance;
    if (source.lockToEmitter !== undefined) options.lockToEmitter = source.lockToEmitter;
    if (source.maxParticles !== undefined) options.maxParticles = source.maxParticles;
    if (source.opacityEnd !== undefined) options.opacityEnd = source.opacityEnd;
    if (source.opacityEndVariance !== undefined) options.opacityEndVariance = source.opacityEndVariance;
    if (source.opacityStart !== undefined) options.opacityStart = source.opacityStart;
    if (source.opacityStartVariance !== undefined) options.opacityStartVariance = source.opacityStartVariance;
    if (source.orientation !== undefined) options.orientation = source.orientation;
    if (source.orientationFixedRotation !== undefined) options.orientationFixedRotation = toVector3(source.orientationFixedRotation);
    if (source.positionVariance !== undefined) options.positionVariance = toVector3(source.positionVariance);
    if (source.rate !== undefined) options.rate = source.rate;
    if (source.rateVariance !== undefined) options.rateVariance = source.rateVariance;
    if (source.sizeEnd !== undefined) options.sizeEnd = source.sizeEnd;
    if (source.sizeEndVariance !== undefined) options.sizeEndVariance = source.sizeEndVariance;
    if (source.sizeStart !== undefined) options.sizeStart = source.sizeStart;
    if (source.sizeStartVariance !== undefined) options.sizeStartVariance = source.sizeStartVariance;
    if (source.transparent !== undefined) options.transparent = source.transparent;
    if (source.velocity !== undefined) options.velocity = toVector3(source.velocity);
    if (source.velocityVariance !== undefined) options.velocityVariance = toVector3(source.velocityVariance);

    // Flipbook
    if (source.flipbookColumns !== undefined && source.flipbookRows !== undefined) {
      options.flipbook = {
        columns: source.flipbookColumns,
        rows: source.flipbookRows,
        fps: source.flipbookFps,
        loop: source.flipbookLoop,
      };
    }

    return options;
  }

  private _applyTransientEvent(particleEmitter: ParticleEmitter, source: DeserializedParticleEmitter): void {
    // Handle stop commands
    if (source.stopAll) {
      particleEmitter.stopAll();
    }

    if (source.stopHandle !== undefined) {
      particleEmitter.stop(source.stopHandle);
    }

    // Handle play command
    if (source.playHandle !== undefined) {
      // Build emit options
      const emitOptions: { entityId?: number; entityNodeName?: string; offset?: Vector3Like; position?: Vector3Like } = {};
      if (source.playEntityId !== undefined) emitOptions.entityId = source.playEntityId;
      if (source.playEntityNodeName !== undefined) emitOptions.entityNodeName = source.playEntityNodeName;
      if (source.playOffset !== undefined) emitOptions.offset = source.playOffset;
      if (source.playPosition !== undefined) emitOptions.position = source.playPosition;

      // Build overrides from play* fields
      const overrides: ParticleEmitterEmissionOverrides = {};
      if (source.playLifetime !== undefined) overrides.lifetime = source.playLifetime;
      if (source.playLifetimeVariance !== undefined) overrides.lifetimeVariance = source.playLifetimeVariance;
      if (source.playColorStart !== undefined) overrides.colorStart = source.playColorStart;
      if (source.playColorStartVariance !== undefined) overrides.colorStartVariance = source.playColorStartVariance;
      if (source.playColorEnd !== undefined) overrides.colorEnd = source.playColorEnd;
      if (source.playColorEndVariance !== undefined) overrides.colorEndVariance = source.playColorEndVariance;
      if (source.playColorIntensityStart !== undefined) overrides.colorIntensityStart = source.playColorIntensityStart;
      if (source.playColorIntensityStartVariance !== undefined) overrides.colorIntensityStartVariance = source.playColorIntensityStartVariance;
      if (source.playColorIntensityEnd !== undefined) overrides.colorIntensityEnd = source.playColorIntensityEnd;
      if (source.playColorIntensityEndVariance !== undefined) overrides.colorIntensityEndVariance = source.playColorIntensityEndVariance;
      if (source.playPositionVariance !== undefined) overrides.positionVariance = toVector3(source.playPositionVariance);
      if (source.playSizeStart !== undefined) overrides.sizeStart = source.playSizeStart;
      if (source.playSizeStartVariance !== undefined) overrides.sizeStartVariance = source.playSizeStartVariance;
      if (source.playSizeEnd !== undefined) overrides.sizeEnd = source.playSizeEnd;
      if (source.playSizeEndVariance !== undefined) overrides.sizeEndVariance = source.playSizeEndVariance;
      if (source.playOpacityStart !== undefined) overrides.opacityStart = source.playOpacityStart;
      if (source.playOpacityStartVariance !== undefined) overrides.opacityStartVariance = source.playOpacityStartVariance;
      if (source.playOpacityEnd !== undefined) overrides.opacityEnd = source.playOpacityEnd;
      if (source.playOpacityEndVariance !== undefined) overrides.opacityEndVariance = source.playOpacityEndVariance;
      if (source.playVelocity !== undefined) overrides.velocity = toVector3(source.playVelocity);
      if (source.playVelocityVariance !== undefined) overrides.velocityVariance = toVector3(source.playVelocityVariance);

      // Play options
      const playOptions: { count?: number; rate?: number } = {};
      if (source.playCount !== undefined) playOptions.count = source.playCount;
      if (source.playRate !== undefined) playOptions.rate = source.playRate;

      particleEmitter.play(
        source.playHandle,
        Object.keys(emitOptions).length > 0 ? emitOptions : undefined,
        Object.keys(overrides).length > 0 ? overrides : undefined,
        Object.keys(playOptions).length > 0 ? playOptions : undefined,
      );
    }
  }

  private _queuePendingEmitterUpdate(source: DeserializedParticleEmitter): void {
    if (source.id === undefined) {
      return;
    }

    if (!this._pendingEmitterUpdates.has(source.id)) {
      this._pendingEmitterUpdates.set(source.id, []);
    }

    this._pendingEmitterUpdates.get(source.id)!.push(source);
  }

  private _applyUpdateToExistingEmitter(particleEmitter: ParticleEmitter, source: DeserializedParticleEmitter): void {
    if (source.attachedToEntityId !== undefined) {
      particleEmitter.setAttachedToEntityId(source.attachedToEntityId);
    }

    if (source.attachedToEntityNodeName !== undefined) {
      particleEmitter.setAttachedToEntityNodeName(source.attachedToEntityNodeName);
    }

    if (source.offset !== undefined) {
      particleEmitter.setOffset(source.offset);
    }

    if (source.position !== undefined) {
      particleEmitter.setPosition(source.position);
    }

    if (source.textureUri !== undefined) {
      particleEmitter.setTextureUri(source.textureUri);
    }

    const emitterCoreOptions = this._toCoreOptions(source);
    if (Object.keys(emitterCoreOptions).length > 0) {
      particleEmitter.setEmitterCoreOptions(emitterCoreOptions);
    }

    if (source.paused !== undefined) {
      if (source.paused) {
        particleEmitter.pause();
      } else {
        particleEmitter.restart();
      }
    }

    this._applyTransientEvent(particleEmitter, source);

    if (source.burst !== undefined) {
      particleEmitter.burst(source.burst);
    }
  }

  private _updateParticleEmitter = (deserializedParticleEmitter: DeserializedParticleEmitter): void => {
    let particleEmitter = this._particleEmitters.get(deserializedParticleEmitter.id);

    if (!particleEmitter) {
      // Create new particles
      if (
        deserializedParticleEmitter.id === undefined ||
        deserializedParticleEmitter.textureUri === undefined
      ) {
        if (deserializedParticleEmitter.id !== undefined) {
          this._queuePendingEmitterUpdate(deserializedParticleEmitter);
        }
        return;
      }

      particleEmitter = new ParticleEmitter(this._game, {
        id: deserializedParticleEmitter.id,
        attachedToEntityId: deserializedParticleEmitter.attachedToEntityId,
        attachedToEntityNodeName: deserializedParticleEmitter.attachedToEntityNodeName,
        offset: deserializedParticleEmitter.offset,
        position: deserializedParticleEmitter.position,
        textureUri: deserializedParticleEmitter.textureUri,
        emitterCoreOptions: this._toCoreOptions(deserializedParticleEmitter),
      });

      this._particleEmitters.set(particleEmitter.id, particleEmitter);

      const pendingUpdates = this._pendingEmitterUpdates.get(particleEmitter.id);
      if (pendingUpdates) {
        this._pendingEmitterUpdates.delete(particleEmitter.id);
        for (const pendingUpdate of pendingUpdates) {
          if (pendingUpdate.removed) {
            particleEmitter.dispose();
            this._particleEmitters.delete(particleEmitter.id);
            return;
          }
          this._applyUpdateToExistingEmitter(particleEmitter, pendingUpdate);
        }
      }
    }

    if (deserializedParticleEmitter.removed) {
      this._pendingEmitterUpdates.delete(particleEmitter.id);
      particleEmitter.dispose();
      this._particleEmitters.delete(particleEmitter.id);
      return;
    }

    this._applyUpdateToExistingEmitter(particleEmitter, deserializedParticleEmitter);
  }
}
